import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Camera, CheckCircle2, Loader2, RefreshCw, ScanFace } from "lucide-react";
import { loadFaceModels, detectEnrollFace } from "@/lib/faceapi";

// Captura facial no totem: câmera frontal → foto + descriptor → onSave
export const KioskFaceCapture = ({ onSave, onCancel, saving }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [captured, setCaptured] = useState(null);

  const stop = () => { streamRef.current?.getTracks().forEach((t) => t.stop()); streamRef.current = null; };
  const start = async () => {
    if (!navigator?.mediaDevices?.getUserMedia || !window.isSecureContext) return toast.error("Câmera indisponível — abra o totem pelo link https.");
    try {
      let s;
      try { s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: "user" }, width: { ideal: 640 }, height: { ideal: 480 } }, audio: false }); }
      catch { s = await navigator.mediaDevices.getUserMedia({ video: true, audio: false }); }
      streamRef.current = s;
      if (videoRef.current) { videoRef.current.srcObject = s; videoRef.current.play().catch(() => {}); }
    } catch (e) {
      toast.error(e?.name === "NotAllowedError" ? "Permissão da câmera negada — libere nas configurações do navegador." : "Não foi possível abrir a câmera.");
    }
  };

  useEffect(() => {
    loadFaceModels().then(() => setReady(true)).catch(() => toast.error("Falha ao carregar o reconhecimento facial"));
    start();
    return stop;
  }, []);

  const capture = async () => {
    const v = videoRef.current, c = canvasRef.current;
    if (!v?.videoWidth) return toast.error("Aguarde a câmera abrir");
    setBusy(true);
    try {
      c.width = v.videoWidth; c.height = v.videoHeight;
      c.getContext("2d").drawImage(v, 0, 0);
      const desc = await detectEnrollFace(v);
      if (desc.problem) return toast.error(desc.problem);
      setCaptured({ photo: c.toDataURL("image/jpeg", 0.75), descriptor: desc.descriptor });
      stop();
    } finally { setBusy(false); }
  };
  const retake = () => { setCaptured(null); setTimeout(start, 50); };

  return (
    <div className="w-full max-w-xl text-center" data-testid="kiosk-face-stage">
      <h1 className="font-heading text-4xl font-black uppercase mb-2 text-rose-500 flex items-center justify-center gap-3"><ScanFace className="w-9 h-9"/>Cadastrar facial</h1>
      <p className="text-white/60 mb-6">Tire óculos/boné, centralize o rosto e toque em Capturar.</p>
      {captured
        ? <img src={captured.photo} alt="Sua foto" className="w-full max-w-md mx-auto rounded-2xl border-4 border-emerald-500" data-testid="kiosk-face-captured"/>
        : <video ref={videoRef} autoPlay playsInline muted className="w-full max-w-md mx-auto rounded-2xl border-4 border-rose-500 -scale-x-100" data-testid="kiosk-face-video"/>}
      <canvas ref={canvasRef} className="hidden"/>
      <div className="mt-6 grid grid-cols-2 gap-3">
        <Button variant="ghost" onClick={() => { stop(); onCancel(); }} className="h-16 text-white/70 hover:text-white hover:bg-white/10 uppercase font-bold" data-testid="kiosk-face-cancel">Cancelar</Button>
        {captured ? (
          <div className="grid grid-cols-2 gap-3">
            <Button onClick={retake} disabled={saving} className="h-16 bg-white/10 hover:bg-white/20 text-white uppercase font-bold" data-testid="kiosk-face-retake"><RefreshCw className="w-5 h-5"/></Button>
            <Button onClick={() => onSave(captured)} disabled={saving} className="h-16 bg-emerald-600 hover:bg-emerald-700 text-white uppercase font-black" data-testid="kiosk-face-save">
              {saving ? <Loader2 className="animate-spin"/> : <><CheckCircle2 className="w-5 h-5 mr-1"/>Salvar</>}
            </Button>
          </div>
        ) : (
          <Button onClick={capture} disabled={!ready || busy} className="h-16 bg-gradient-to-r from-rose-600 to-rose-500 text-white uppercase font-black" data-testid="kiosk-face-capture">
            {!ready || busy ? <Loader2 className="animate-spin"/> : <><Camera className="w-5 h-5 mr-2"/>Capturar</>}
          </Button>
        )}
      </div>
    </div>
  );
};
